import * as React from 'react';

import { parseZonedDateTime } from '@internationalized/date';
import {
  AsyncBoundary,
  Button,
  Card,
  Col,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  LocalizedDate,
  Row,
  cn,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideHistory,
  LucideRotateCcw,
  LucideEye,
  LucideEyeOff,
  LucideFileJson,
} from 'lucide-react';

import type { AutomationVersionRecord } from '../../_graphql/queryAutomationVersions';
import {
  $editorAutomationId,
  $remoteEditorAutomation,
  $saveFlow,
} from '../../_state/automationEditor.state';
import { $remoteAutomationVersions } from '../../_state/automationVersions.state';
import { useTranslations } from '../../i18n/useTranslations';

const formatUserName = (
  user: AutomationVersionRecord['createdByUser'],
  fallback: string,
): string => {
  if (!user) return fallback;
  return `${user.firstName} ${user.lastName}`.trim() || fallback;
};

export const VersionHistoryPanel: React.FC = () => {
  const t = useTranslations();
  const toast = useToast();

  const automationId = useAtomValue($editorAutomationId);
  const automation = useAtomValue($remoteEditorAutomation).data;
  const { data, isLoading, error } = useAtomValue($remoteAutomationVersions);
  const saveFlow = useSetAtom($saveFlow);

  const [expandedVersionId, setExpandedVersionId] = React.useState<
    string | null
  >(null);
  const [restoreVersion, setRestoreVersion] =
    React.useState<AutomationVersionRecord | null>(null);
  const [isRestoring, setIsRestoring] = React.useState(false);

  const handleToggleSnapshot = React.useCallback((versionId: string) => {
    setExpandedVersionId((prev) => (prev === versionId ? null : versionId));
  }, []);

  const handleRestoreConfirm = React.useCallback(async () => {
    if (!restoreVersion || !automationId || !automation) return;

    const snapshot = restoreVersion.flowSnapshot;
    if (!snapshot) {
      toast.error(t.versionHistory.messages.restoreError);
      setRestoreVersion(null);
      return;
    }

    setIsRestoring(true);
    try {
      const nodes = (snapshot.nodes as unknown[]) ?? [];
      const edges = (snapshot.edges as unknown[]) ?? [];

      await saveFlow({
        id: automationId,
        name: automation.name,
        description: automation.description,
        nodes: nodes as Parameters<typeof saveFlow>[0]['nodes'],
        edges: edges as Parameters<typeof saveFlow>[0]['edges'],
      });

      toast.success(t.versionHistory.messages.restoreSuccess);
      setRestoreVersion(null);
    } catch {
      toast.error(t.versionHistory.messages.restoreError);
    } finally {
      setIsRestoring(false);
    }
  }, [restoreVersion, automationId, automation, saveFlow, toast, t]);

  const versions = data?.versions ?? [];
  const total = data?.total ?? 0;

  return (
    <Col className={cn('@VersionHistoryPanel', 'gap-4 p-6')}>
      <div>
        <Row className="items-center gap-2">
          <LucideHistory className="size-5 text-neutral/60" />
          <h2 className="text-lg font-semibold">{t.versionHistory.title}</h2>
        </Row>
        <p className="mt-1 text-sm text-neutral/60">
          {total}{' '}
          {total === 1
            ? t.versionHistory.versionCount
            : t.versionHistory.versionCountPlural}
        </p>
      </div>

      <AsyncBoundary error={error} loading={isLoading}>
        <Col className="gap-3">
          {versions.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mb-4 text-neutral/40">
                <LucideHistory className="mx-auto size-16" />
              </div>
              <h3 className="mb-2 text-lg font-medium">
                {t.versionHistory.noVersions}
              </h3>
              <p className="text-sm text-neutral/60">
                {t.versionHistory.noVersionsDescription}
              </p>
            </div>
          ) : (
            versions.map((version) => (
              <VersionCard
                key={version.id}
                version={version}
                isExpanded={expandedVersionId === version.id}
                onToggleSnapshot={handleToggleSnapshot}
                onRestore={setRestoreVersion}
              />
            ))
          )}
        </Col>
      </AsyncBoundary>

      <RestoreConfirmDialog
        isOpen={restoreVersion !== null}
        onOpenChange={(open) => {
          if (!open) setRestoreVersion(null);
        }}
        versionNumber={restoreVersion?.versionNumber ?? 0}
        onConfirm={handleRestoreConfirm}
        isRestoring={isRestoring}
      />
    </Col>
  );
};

type VersionCardProps = {
  version: AutomationVersionRecord;
  isExpanded: boolean;
  onToggleSnapshot: (id: string) => void;
  onRestore: (version: AutomationVersionRecord) => void;
};

const VersionCard: React.FC<VersionCardProps> = ({
  version,
  isExpanded,
  onToggleSnapshot,
  onRestore,
}) => {
  const t = useTranslations();

  const createdAt = React.useMemo(() => {
    try {
      return parseZonedDateTime(version.timestamps.createdAt);
    } catch {
      return null;
    }
  }, [version.timestamps.createdAt]);

  return (
    <Card className="@VersionCard">
      <Col className="gap-3">
        <Row className="items-start justify-between gap-4">
          <Col className="flex-1 gap-1">
            <Row className="items-center gap-2">
              <span className="text-sm font-semibold">
                {t.versionHistory.versionLabel(version.versionNumber)}
              </span>
            </Row>
            <Row className="flex-wrap items-center gap-3 text-xs text-neutral/60">
              {createdAt && (
                <span>
                  <LocalizedDate date={createdAt} />
                </span>
              )}
              <span>
                {t.versionHistory.createdBy}{' '}
                {formatUserName(
                  version.createdByUser,
                  t.versionHistory.unknown,
                )}
              </span>
            </Row>
            {version.description && (
              <p className="mt-1 text-sm text-neutral/70">
                {version.description}
              </p>
            )}
          </Col>
          <Row className="gap-1">
            <Button
              variant="ghost"
              size="small"
              onPress={() => onToggleSnapshot(version.id)}
            >
              {isExpanded ? (
                <LucideEyeOff className="mr-1 size-4" />
              ) : (
                <LucideEye className="mr-1 size-4" />
              )}
              {t.versionHistory.actions.view}
            </Button>
            <Button
              variant="ghost"
              size="small"
              onPress={() => onRestore(version)}
              isDisabled={!version.flowSnapshot}
            >
              <LucideRotateCcw className="mr-1 size-4" />
              {t.versionHistory.actions.restore}
            </Button>
          </Row>
        </Row>

        {isExpanded && version.flowSnapshot && (
          <div className="rounded border border-neutral/20 bg-neutral/5 p-3">
            <Row className="mb-2 items-center gap-1 text-xs font-medium text-neutral/60">
              <LucideFileJson className="size-3" />
              {t.versionHistory.flowSnapshot}
            </Row>
            <pre className="max-h-64 overflow-auto text-xs text-neutral/80">
              {JSON.stringify(version.flowSnapshot, null, 2)}
            </pre>
          </div>
        )}
      </Col>
    </Card>
  );
};

type RestoreConfirmDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  versionNumber: number;
  onConfirm: () => void | Promise<void>;
  isRestoring: boolean;
};

const RestoreConfirmDialog: React.FC<RestoreConfirmDialogProps> = ({
  isOpen,
  onOpenChange,
  versionNumber,
  onConfirm,
  isRestoring,
}) => {
  const t = useTranslations();

  const handleClose = () => {
    if (!isRestoring) {
      onOpenChange(false);
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@RestoreConfirmDialog"
    >
      <DialogContent position="center" className="w-full min-w-[32rem]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <LucideRotateCcw className="size-5" />
            {t.versionHistory.restoreDialog.title}
          </DialogTitle>
        </DialogHeader>

        <div className="p-4">
          <p className="text-sm text-neutral/70">
            {t.versionHistory.restoreDialog.message(versionNumber)}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onPress={handleClose}
            isDisabled={isRestoring}
          >
            {t.versionHistory.restoreDialog.cancel}
          </Button>
          <Button
            variant="solid"
            color="primary"
            onPress={onConfirm}
            isDisabled={isRestoring}
            isLoading={isRestoring}
          >
            {t.versionHistory.restoreDialog.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
